import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import JSZip from 'jszip';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import api from '../services/api';
import DateInput from '../components/DateInput';

const buildPdfWorkerSrc = () => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const base = publicUrl ? (window.location.origin + (publicUrl.startsWith('/') ? '' : '/') + publicUrl) : window.location.origin;
  return `${base}/pdf.worker.min.js`.replace(/\/+/g, '/');
};
if (typeof pdfjs?.GlobalWorkerOptions !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = buildPdfWorkerSrc();
}

const styles = {
  container: { padding: '20px' },
  filtersContainer: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e9ecef',
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#495057' },
  input: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '0.95rem' },
  select: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '140px' },
  button: { padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 500 },
  searchButton: { background: '#0d6efd', color: '#fff' },
  cancelButton: { background: '#dc3545', color: '#fff', fontSize: '0.85rem', padding: '6px 12px' },
  editButton: { background: '#17a2b8', color: '#fff', fontSize: '0.85rem', padding: '6px 12px', marginLeft: '4px' },
  cancelRequestButton: { background: '#fd7e14', color: '#fff', fontSize: '0.85rem', padding: '6px 12px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #ddd' },
  modalBody: { marginBottom: '20px' },
  formGroup: { marginBottom: '15px' },
  formLabel: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
  formInput: { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' },
  formRow: { display: 'flex', gap: '10px', marginBottom: '15px' },
  formCol: { flex: 1 },
  fileInput: { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  modalError: {
    marginBottom: '12px',
    padding: '10px 12px',
    background: '#fff5f5',
    border: '1px solid #f5c6cb',
    borderRadius: '6px',
    color: '#c82333',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  saveButton: { backgroundColor: '#28a745', color: 'white', padding: '8px 16px', borderRadius: '6px' },
  tableContainer: { marginTop: '16px', overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { borderBottom: '2px solid #dee2e6', padding: '10px', background: '#f8f9fa', textAlign: 'center', fontWeight: 600 },
  thSortable: { cursor: 'pointer', userSelect: 'none' },
  td: { borderBottom: '1px solid #eee', padding: '10px', textAlign: 'center' },
  fileLink: { color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline', marginRight: '8px' },
  countDisplay: { fontSize: '0.95rem', fontWeight: 600, color: '#333', marginBottom: '10px' },
  filePreviewModal: { background: 'white', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  filePreviewBody: { flex: 1, overflow: 'auto', minHeight: 0 },
  filePreviewContainer: { width: '100%', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pdfObject: { width: '100%', minHeight: 500 },
  filePreview: { maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' },
};

const statusOptions = [
  { value: '', label: '전체' },
  { value: '접수중', label: '접수중' },
  { value: '승인', label: '승인' },
  { value: '반려', label: '반려' },
  { value: '취소', label: '취소' },
  { value: '취소_접수', label: '취소(접수)' },
  { value: '취소_승인', label: '취소(승인)' },
];

const toYmdLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** 연차신청 조회 기본 사용일자: 전달 1일 ~ 당해연도 12월 31일 (조회일 기준) */
const getInquiryDefaultUseDates = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const from = new Date(y, m - 1, 1);
  const to = new Date(y, 11, 31);
  return {
    from: toYmdLocal(from),
    to: toYmdLocal(to),
  };
};

/** 사용일자 컬럼 정렬용: 표시와 같이 가장 이른 사용일 기준 타임스탬프 */
function getUsageEarliestDateMs(app) {
  const details = app?.details;
  const times = [];
  if (Array.isArray(details) && details.length > 0) {
    for (const d of details) {
      if (!d?.date) continue;
      const t = new Date(d.date).getTime();
      if (Number.isFinite(t)) times.push(t);
    }
  }
  if (times.length === 0 && app?.date) {
    const t = new Date(app.date).getTime();
    if (Number.isFinite(t)) times.push(t);
  }
  if (times.length === 0) return 0;
  return Math.min(...times);
}

const LeaveApplicationInquiryPage = () => {
  const currentYear = new Date().getFullYear();
  const defaultUseDates = useMemo(() => getInquiryDefaultUseDates(), []);
  const [year, setYear] = useState(currentYear);
  const [useDateFrom, setUseDateFrom] = useState(defaultUseDates.from);
  const [useDateTo, setUseDateTo] = useState(defaultUseDates.to);
  const [projectId, setProjectId] = useState('');
  const [district, setDistrict] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [status, setStatus] = useState('');
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingApp, setEditingApp] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', hours: '3', userComment: '', files: [] });
  const [editLeaveSummary, setEditLeaveSummary] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [cancelRequestApp, setCancelRequestApp] = useState(null);
  const [cancelRequestComment, setCancelRequestComment] = useState('');
  const [cancelRequestLoading, setCancelRequestLoading] = useState(false);
  const [cancelRequestError, setCancelRequestError] = useState('');
  const cancelRequestAbortRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewFileName, setPreviewFileName] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewObjectUrl, setPreviewObjectUrl] = useState('');
  const [previewPdfData, setPreviewPdfData] = useState(null);
  const [previewPdfTotalPages, setPreviewPdfTotalPages] = useState(0);
  const [previewPdfPage, setPreviewPdfPage] = useState(1);
  const previewUrlRef = useRef('');
  const previewContainerRef = useRef(null);

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
  }, []);

  const fetchPreviewFile = useCallback(async (filename) => {
    if (!filename) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.get(`/leave/applications/files/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      revokePreviewUrl();
      const blob = response.data;
      const isPdf = filename.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        setPreviewPdfData(blob);
        setPreviewPdfTotalPages(0);
        setPreviewPdfPage(1);
        setPreviewObjectUrl('');
        previewUrlRef.current = '';
      } else {
        const objectUrl = URL.createObjectURL(blob);
        previewUrlRef.current = objectUrl;
        setPreviewObjectUrl(objectUrl);
        setPreviewPdfData(null);
        setPreviewPdfTotalPages(0);
      }
    } catch (error) {
      setPreviewError(error.response?.data?.message || '파일을 불러오는 중 오류가 발생했습니다.');
      setPreviewObjectUrl('');
      setPreviewPdfData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [revokePreviewUrl]);

  useEffect(() => {
    if (isPreviewModalOpen && previewFileName) {
      fetchPreviewFile(previewFileName);
    }
  }, [isPreviewModalOpen, previewFileName, fetchPreviewFile]);

  useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

  const handleFilePreview = (files, initialFilename) => {
    if (files && files.length > 0) {
      setPreviewFiles(files);
      setPreviewFileName(initialFilename || files[0].filename);
      setPreviewError('');
      setPreviewObjectUrl('');
      setPreviewPdfData(null);
      setPreviewPdfTotalPages(0);
      setPreviewPdfPage(1);
      setIsPreviewModalOpen(true);
    }
  };

  const handleClosePreview = () => {
    revokePreviewUrl();
    setIsPreviewModalOpen(false);
    setPreviewFileName(null);
    setPreviewFiles([]);
    setPreviewError('');
    setPreviewObjectUrl('');
    setPreviewPdfData(null);
  };

  const handlePreviewFileChange = (filename) => {
    if (!filename) return;
    setPreviewFileName(filename);
    setPreviewError('');
    setPreviewObjectUrl('');
    setPreviewPdfData(null);
    setPreviewPdfTotalPages(0);
    setPreviewPdfPage(1);
  };

  const handlePdfDocumentLoad = ({ numPages }) => {
    setPreviewPdfTotalPages(numPages || 1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedApplications = useMemo(() => {
    if (!sortColumn || applications.length === 0) return applications;
    const sorted = [...applications].sort((a, b) => {
      let valA;
      let valB;
      switch (sortColumn) {
        case 'createdAt':
          valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return valA - valB;
        case 'name':
          return (a.participant?.name || '').localeCompare(b.participant?.name || '', 'ko');
        case 'usageDate':
          valA = getUsageEarliestDateMs(a);
          valB = getUsageEarliestDateMs(b);
          return valA - valB;
        default:
          return 0;
      }
    });
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [applications, sortColumn, sortDirection]);

  const toggleSelect = (appId) => {
    const next = new Set(selectedIds);
    if (next.has(appId)) next.delete(appId);
    else next.add(appId);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedApplications.length && sortedApplications.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedApplications.map(a => a.id)));
    }
  };

  const selectedAppsWithFiles = useMemo(() => {
    return sortedApplications.filter(a => selectedIds.has(a.id) && Array.isArray(a.files) && a.files.length > 0);
  }, [sortedApplications, selectedIds]);

  const totalFileCount = useMemo(() => {
    return selectedAppsWithFiles.reduce((sum, a) => sum + (a.files?.length || 0), 0);
  }, [selectedAppsWithFiles]);

  const handleBulkDownload = async () => {
    if (totalFileCount === 0) {
      alert('선택한 항목에 첨부파일이 없습니다.');
      return;
    }
    setBulkDownloadLoading(true);
    setError('');
    setMessage('');
    try {
      const zip = new JSZip();
      const usedNames = new Set();
      const safeName = (prefix, name) => {
        let base = `${prefix}_${name}`.replace(/[/\\?%*:|"<>]/g, '_');
        if (usedNames.has(base)) {
          let i = 1;
          const ext = (name.match(/\.[^.]+$/) || [])[0] || '';
          const nameNoExt = base.replace(/\.[^.]+$/, '');
          while (usedNames.has(`${nameNoExt}_${i}${ext}`)) i++;
          base = `${nameNoExt}_${i}${ext}`;
        }
        usedNames.add(base);
        return base;
      };
      for (const app of selectedAppsWithFiles) {
        const prefix = `${formatDate(app.date)}_${(app.participant?.name || 'unknown').replace(/[/\\?%*:|"<>]/g, '_')}`;
        for (const f of app.files) {
          const res = await api.get(`/leave/applications/files/${encodeURIComponent(f.filename)}`, {
            responseType: 'blob',
            headers: sessionStorage.getItem('token') ? { Authorization: `Bearer ${sessionStorage.getItem('token')}` } : {}
          });
          const name = f.originalname || f.filename || 'file';
          zip.file(safeName(prefix, name), res.data);
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `연차신청_첨부파일_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage(`${totalFileCount}개 파일이 ZIP으로 다운로드되었습니다.`);
    } catch (err) {
      setError(err.response?.data?.message || '일괄 다운로드에 실패했습니다.');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [applications]);

  /** 신청날짜·성명·사용일자만 정렬 (↑ 오름차순 · ↓ 내림차순) */
  const SortableTh = ({ column, label }) => {
    const active = sortColumn === column;
    return (
      <th
        style={{
          ...styles.th,
          ...styles.thSortable,
          ...(active ? { background: '#eef2f7' } : {}),
        }}
        onClick={() => handleSort(column)}
        title={active ? (sortDirection === 'asc' ? '오름차순 — 클릭 시 내림차순' : '내림차순 — 클릭 시 오름차순') : '클릭하여 정렬'}
      >
        <span>{label}</span>
        {active && (
        <span
          style={{
            marginLeft: 6,
            color: '#0d6efd',
            fontSize: '0.9rem',
            fontWeight: 700,
          }}
          aria-hidden
        >
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
        )}
      </th>
    );
  };

  const fetchProjects = useCallback(async () => {
    if (!year) return;
    try {
      const res = await api.get('/projects', { params: { year } });
      setProjects(res.data || []);
    } catch (err) {
      setProjects([]);
    }
  }, [year]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  /** 검색·엑셀 출력에 동일하게 사용 */
  const buildListParams = useCallback(() => {
    const params = {};
    if (useDateFrom) params.dateFrom = useDateFrom;
    if (useDateTo) params.dateTo = useDateTo;
    if (projectId) params.projectId = projectId;
    if (district.trim()) params.district = district.trim();
    if (participantName.trim()) params.participantName = participantName.trim();
    if (status) {
      if (status === '취소_접수') {
        params.status = '취소';
        params.cancelSubtype = '접수';
      } else if (status === '취소_승인') {
        params.status = '취소';
        params.cancelSubtype = '승인';
      } else {
        params.status = status;
      }
    }
    if (year && !useDateFrom && !useDateTo) params.year = year;
    return params;
  }, [year, useDateFrom, useDateTo, projectId, district, participantName, status]);

  /**
   * @param {{ silent?: boolean }} [options] silent=true 이면 전역 로딩/메시지 초기화 없이 목록만 갱신(철회·수정 등 직후)
   */
  const fetchApplications = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setLoading(true);
      setError('');
      setMessage('');
    }
    try {
      const params = buildListParams();

      const res = await api.get('/leave/applications', { params });
      setApplications(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '연차 신청 목록 조회 실패');
      setApplications([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [buildListParams]);

  const handleExcelExport = useCallback(async () => {
    if (exportLoading || loading) return;
    setExportLoading(true);
    setMessage('');
    setError('');
    try {
      const params = buildListParams();
      const response = await api.get('/leave/applications/export', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      let downloadName = 'leave_applications.xlsx';
      if (cd) {
        const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(cd);
        if (m && m[1]) {
          downloadName = m[1].replace(/['"]/g, '').trim();
        }
      }
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage('엑셀 파일이 다운로드되었습니다.');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('엑셀로 내보낼 데이터가 없습니다.');
        return;
      }
      let msg = '엑셀 출력에 실패했습니다.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          if (j?.message) msg = j.message;
        } catch (_) {
          /* ignore */
        }
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      setError(msg);
    } finally {
      setExportLoading(false);
    }
  }, [buildListParams, exportLoading, loading]);

  const handleSearch = () => {
    fetchApplications();
  };

  const handleCancelAccept = async (app) => {
    if (app.status !== '접수중') return;
    if (!window.confirm('해당 연차 신청을 접수 취소하시겠습니까?')) return;
    try {
      await api.put(`/leave/applications/${app.id}`, { status: '취소', comment: '접수 취소' });
      setMessage('접수가 취소되었습니다.');
      fetchApplications({ silent: true });
    } catch (err) {
      setError(err.response?.data?.message || '접수 취소 실패');
    }
  };

  const handleOpenCancelRequestModal = (app) => {
    setCancelRequestApp(app);
    setCancelRequestComment('');
    setCancelRequestError('');
  };

  const handleCloseCancelRequestModal = () => {
    if (cancelRequestAbortRef.current) {
      cancelRequestAbortRef.current.abort();
      cancelRequestAbortRef.current = null;
    }
    setCancelRequestApp(null);
    setCancelRequestComment('');
    setCancelRequestLoading(false);
    setCancelRequestError('');
  };

  const handleSubmitCancelRequest = async () => {
    if (!cancelRequestApp) return;
    if (cancelRequestAbortRef.current) {
      cancelRequestAbortRef.current.abort();
      cancelRequestAbortRef.current = null;
    }
    const ac = new AbortController();
    cancelRequestAbortRef.current = ac;
    setCancelRequestLoading(true);
    setCancelRequestError('');
    setError('');
    try {
      await api.post(
        '/leave/cancellation-requests',
        {
          leaveApplicationId: cancelRequestApp.id,
          comment: cancelRequestComment.trim() || undefined,
        },
        { signal: ac.signal }
      );
      cancelRequestAbortRef.current = null;
      setMessage('승인취소 신청이 접수되었습니다.');
      handleCloseCancelRequestModal();
      fetchApplications({ silent: true });
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      const msg = err.response?.data?.message || '승인취소 신청 실패';
      setCancelRequestError(msg);
      setError(msg);
    } finally {
      cancelRequestAbortRef.current = null;
      setCancelRequestLoading(false);
    }
  };

  const handleCancelRequestAcceptCancel = async (cancelRequest) => {
    if (!cancelRequest?.id) return;
    if (!window.confirm('취소신청을 철회하시겠습니까?')) return;
    try {
      await api.put(`/leave/cancellation-requests/${cancelRequest.id}`, { status: '접수취소' });
      setMessage('취소신청이 철회되었습니다.');
      fetchApplications({ silent: true });
    } catch (err) {
      setError(err.response?.data?.message || '철회 실패');
    }
  };

  const handleOpenEdit = async (app) => {
    setEditingApp(app);
    setEditForm({
      date: app.date || '',
      hours: String(app.hours ?? '3'),
      userComment: app.userComment || '',
      files: []
    });
    setEditLeaveSummary(null);
    if (app.participantId) {
      try {
        const res = await api.get(`/report/individual/${app.participantId}`, {
          params: { year: app.date ? new Date(app.date).getFullYear() : new Date().getFullYear() }
        });
        setEditLeaveSummary(res.data?.leaveSummary || null);
      } catch {
        setEditLeaveSummary(null);
      }
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'files') {
      setEditForm((prev) => ({ ...prev, files: Array.from(files || []) }));
    } else {
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditSubmit = async () => {
    if (!editingApp) return;
    if (!editForm.date || !editForm.hours) {
      setError('사용일자와 사용시간을 입력해주세요.');
      return;
    }
    if (!editForm.files || editForm.files.length === 0) {
      const hasExisting = Array.isArray(editingApp.files) && editingApp.files.length > 0;
      if (!hasExisting) {
        setError('첨부파일을 첨부해주세요.');
        return;
      }
    }
    setEditLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('date', editForm.date);
      fd.append('hours', editForm.hours);
      fd.append('userComment', editForm.userComment || '');
      if (editForm.files && editForm.files.length > 0) {
        editForm.files.forEach((f) => fd.append('files', f));
      }
      await api.put(`/leave/applications/${editingApp.id}/edit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage('연차 신청이 수정되었습니다.');
      setEditingApp(null);
      fetchApplications({ silent: true });
    } catch (err) {
      setError(err.response?.data?.message || '수정 실패');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditingApp(null);
    setEditForm({ date: '', hours: '3', userComment: '', files: [] });
    setEditLeaveSummary(null);
  };

  const handleFileDownload = (filename, originalname) => {
    api.get(`/leave/applications/files/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = originalname ? decodeURIComponent(originalname) : filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('파일 다운로드 중 오류가 발생했습니다.'));
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('ko-KR');
  };

  const formatMonthDay = (ymd) => {
    if (!ymd) return '-';
    const parts = String(ymd).split('-').map((v) => parseInt(v, 10));
    const m = parts[1];
    const d = parts[2];
    if (!Number.isFinite(m) || !Number.isFinite(d)) return ymd;
    return `${m}/${d}`;
  };

  const getUsageDetails = (app) => {
    const details = app?.details;
    if (Array.isArray(details) && details.length > 0) {
      return details
        .map((d) => ({ date: d?.date, hours: parseFloat(d?.hours) || 0 }))
        .filter((d) => d.date && d.hours > 0);
    }
    return [{ date: app?.date, hours: parseFloat(app?.hours) || 0 }].filter((d) => d.date && d.hours > 0);
  };

  const getUsageTotalHours = (app) => {
    const details = getUsageDetails(app);
    return details.reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0);
  };

  const formatUsageHoursKo = (n) => {
    const x = parseFloat(n);
    if (!Number.isFinite(x)) return '0';
    if (Number.isInteger(x)) return String(x);
    const rounded = Math.round(x * 100) / 100;
    return String(rounded);
  };

  /** 예: 3시간(1일), 5시간(2일) */
  const formatUsageTimeCell = (app) => {
    const totalHours = getUsageTotalHours(app);
    const details = getUsageDetails(app);
    const dayCount = new Set(details.map((d) => d.date).filter(Boolean)).size;
    return `${formatUsageHoursKo(totalHours)}시간(${dayCount}일)`;
  };

  const formatUsageDateList = (app) => {
    const details = getUsageDetails(app);
    const sorted = [...details].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const dateList = [];
    const seen = new Set();
    for (const d of sorted) {
      if (seen.has(d.date)) continue;
      seen.add(d.date);
      dateList.push(formatMonthDay(d.date));
    }
    return dateList.length ? dateList.join(', ') : '-';
  };

  const noticeBox = {
    background: '#f0f7ff',
    border: '1px solid #cce5ff',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '0.9rem',
    color: '#004085',
    lineHeight: 1.6,
  };

  return (
    <div style={styles.container}>
      <h2>연차 신청 조회</h2>

      <div style={noticeBox}>
        <strong>안내</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          <li>처음에는 사용일자가 전달 1일~당해연도 12월 31일로 채워집니다.</li>
          <li>연차 승인이 완료된 건은 취소가 필요할 경우 관리자에게 요청해 주세요.</li>
        </ul>
      </div>

      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              style={styles.input}
              value={year}
              min="2020"
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사용일자(시작)</span>
            <DateInput value={useDateFrom} onChange={setUseDateFrom} inputStyle={styles.input} />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사용일자(종료)</span>
            <DateInput value={useDateTo} onChange={setUseDateTo} inputStyle={styles.input} />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업단</span>
            <select style={styles.select} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">전체</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.projectName}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>읍면동</span>
            <input
              type="text"
              style={styles.input}
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="읍면동 검색"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>성명</span>
            <input
              type="text"
              style={styles.input}
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              placeholder="성명 검색"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>상태</span>
            <select style={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {statusOptions.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button style={{ ...styles.button, ...styles.searchButton }} onClick={handleSearch} disabled={loading}>
            {loading ? '조회 중...' : '검색'}
          </button>
        </div>
      </div>

      {message && <p style={{ color: 'green', marginBottom: '8px' }}>{message}</p>}
      {error && <p style={{ color: 'red', marginBottom: '8px' }}>{error}</p>}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span style={styles.countDisplay}>
          {loading ? '조회 중...' : `조회 결과: ${applications.length}건`}
        </span>
        <button
          type="button"
          onClick={handleExcelExport}
          disabled={loading || exportLoading}
          style={{
            ...styles.button,
            ...styles.saveButton,
          }}
        >
          {exportLoading ? '엑셀 생성 중...' : '엑셀 출력'}
        </button>
        <button
          type="button"
          onClick={handleBulkDownload}
          disabled={loading || bulkDownloadLoading || selectedIds.size === 0 || totalFileCount === 0}
          style={{
            ...styles.button,
            ...styles.saveButton,
            opacity: selectedIds.size === 0 || totalFileCount === 0 ? 0.6 : 1
          }}
        >
          {bulkDownloadLoading ? '다운로드 중...' : `선택 항목 첨부파일 일괄 다운로드 (ZIP) ${selectedIds.size > 0 ? `(${totalFileCount}개)` : ''}`}
        </button>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 40 }}>
                <input
                  type="checkbox"
                  checked={sortedApplications.length > 0 && selectedIds.size === sortedApplications.length}
                  onChange={toggleSelectAll}
                  title="전체 선택"
                />
              </th>
              <SortableTh column="createdAt" label="신청날짜" />
              <SortableTh column="name" label="성명" />
              <th style={styles.th}>주민번호</th>
              <th style={styles.th}>사업단</th>
              <SortableTh column="usageDate" label="사용일자" />
              <th style={{ ...styles.th, minWidth: 150, whiteSpace: 'nowrap' }}>사용시간</th>
              <th style={styles.th}>상태</th>
              <th style={styles.th}>반려 사유</th>
              <th style={styles.th}>첨부파일</th>
              <th style={styles.th}>담당자</th>
              <th style={styles.th}>접수상태</th>
            </tr>
          </thead>
          <tbody>
            {applications.length === 0 ? (
              <tr>
                <td colSpan={12} style={styles.td}>조회된 연차 신청이 없습니다.</td>
              </tr>
            ) : (
              sortedApplications.map((app) => (
                <tr key={app.id}>
                  <td style={styles.td}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.id)}
                      onChange={() => toggleSelect(app.id)}
                    />
                  </td>
                  <td style={styles.td}>{formatDate(app.createdAt)}</td>
                  <td style={styles.td}>{app.participant?.name || '-'}</td>
                  <td style={styles.td}>{app.participant?.rrn ? `${String(app.participant.rrn).substring(0, 8)}****` : '-'}</td>
                  <td style={styles.td}>{app.project?.projectName || '-'}</td>
                  <td style={styles.td}>{formatUsageDateList(app)}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formatUsageTimeCell(app)}</td>
                  <td style={styles.td}>
                    {app.status === '취소'
                      ? (app.comment === '접수 취소' ? '취소(접수)' : '취소(승인)')
                      : app.status}
                  </td>
                  <td style={styles.td}>
                    {app.status === '반려' ? (app.comment || '-') : '-'}
                  </td>
                  <td style={styles.td}>
                    {Array.isArray(app.files) && app.files.length > 0
                      ? app.files.map((f, i) => (
                          <span
                            key={i}
                            style={styles.fileLink}
                            onClick={() => handleFilePreview(app.files, f.filename)}
                          >
                            {f.originalname || f.filename || `첨부${i + 1}`}
                          </span>
                        ))
                      : '-'}
                  </td>
                  <td style={styles.td}>{app.participant?.registrar || '-'}</td>
                  <td style={styles.td}>
                    {app.status === '접수중' ? (
                      <>
                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.editButton }}
                          onClick={() => handleOpenEdit(app)}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.cancelButton }}
                          onClick={() => handleCancelAccept(app)}
                        >
                          접수취소
                        </button>
                      </>
                    ) : app.status === '승인' ? (
                      (app.CancellationRequests?.length || 0) > 0 ? (
                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.cancelButton }}
                          onClick={() => handleCancelRequestAcceptCancel(app.CancellationRequests[0])}
                        >
                          취소신청 철회
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.cancelRequestButton }}
                          onClick={() => handleOpenCancelRequestModal(app)}
                        >
                          승인취소신청
                        </button>
                      )
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 파일 미리보기 모달 */}
      {isPreviewModalOpen && previewFileName && (
        <div style={styles.modalOverlay} onClick={handleClosePreview}>
          <div style={styles.filePreviewModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>파일 미리보기</h3>
              <button onClick={handleClosePreview} style={styles.button}>×</button>
            </div>
            <div style={styles.filePreviewBody}>
              {previewFiles.length > 1 && (
                <div style={{ marginBottom: '15px' }}>
                  <label>파일 선택: </label>
                  <select
                    onChange={(e) => handlePreviewFileChange(e.target.value)}
                    value={previewFileName || ''}
                    style={{ marginLeft: '10px', padding: '5px' }}
                  >
                    {previewFiles.map((f, i) => (
                      <option key={i} value={f.filename}>
                        {f.originalname || f.filename || `첨부${i + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div ref={previewContainerRef} style={styles.filePreviewContainer}>
                {previewLoading && <p>파일을 불러오는 중입니다...</p>}
                {previewError && !previewLoading && <p style={{ color: 'red' }}>{previewError}</p>}
                {!previewLoading && !previewError && previewObjectUrl && (
                  (() => {
                    const lower = previewFileName.toLowerCase();
                    if (lower.endsWith('.pdf')) {
                      const pdfUrl = `${previewObjectUrl}#toolbar=0&navpanes=0&scrollbar=1&view=Fit`;
                      return (
                        <object data={pdfUrl} type="application/pdf" style={styles.pdfObject}>
                          <embed src={pdfUrl} type="application/pdf" style={styles.pdfObject} />
                        </object>
                      );
                    }
                    return <img src={previewObjectUrl} style={styles.filePreview} alt="미리보기" />;
                  })()
                )}
                {!previewLoading && !previewError && !previewObjectUrl && previewFileName?.toLowerCase().endsWith('.pdf') && previewPdfData && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    {previewPdfTotalPages > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          onClick={() => setPreviewPdfPage(p => Math.max(1, p - 1))}
                          disabled={previewPdfPage <= 1}
                          style={styles.button}
                        >
                          이전
                        </button>
                        <span>{previewPdfPage} / {previewPdfTotalPages}</span>
                        <button
                          onClick={() => setPreviewPdfPage(p => Math.min(previewPdfTotalPages, p + 1))}
                          disabled={previewPdfPage >= previewPdfTotalPages}
                          style={styles.button}
                        >
                          다음
                        </button>
                      </div>
                    )}
                    <Document
                      file={previewPdfData}
                      onLoadSuccess={handlePdfDocumentLoad}
                      loading="PDF 로딩 중..."
                      error="PDF 로딩 실패"
                    >
                      <Page
                        pageNumber={previewPdfPage}
                        width={Math.min(typeof window !== 'undefined' ? window.innerWidth * 0.7 : 600, 700)}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    </Document>
                  </div>
                )}
                {!previewLoading && !previewError && !previewObjectUrl && !(previewFileName?.toLowerCase().endsWith('.pdf') && previewPdfData) && (
                  <p>파일을 불러올 수 없습니다.</p>
                )}
              </div>
            </div>
            <div style={styles.modalFooter}>
              {previewFiles.length > 0 && (() => {
                const sel = previewFiles.find(f => f.filename === previewFileName);
                return sel ? (
                  <button
                    onClick={() => handleFileDownload(sel.filename, sel.originalname)}
                    style={{ ...styles.button, ...styles.saveButton }}
                  >
                    다운로드
                  </button>
                ) : null;
              })()}
              <button onClick={handleClosePreview} style={styles.button}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 연차 신청 수정 모달 (연차 등록과 동일 UI) */}
      {editingApp && (
        <div style={styles.modalOverlay} onClick={handleCloseEditModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>연차 신청 수정</h3>
              <button onClick={handleCloseEditModal} style={styles.button}>×</button>
            </div>
            <div style={styles.modalBody}>
              {/* 기본 인적사항 */}
              <div style={styles.formRow}>
                <div style={styles.formCol}>
                  <label style={styles.formLabel}>성명</label>
                  <input type="text" value={editingApp.participant?.name || ''} readOnly style={styles.formInput} />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.formLabel}>주민번호</label>
                  <input type="text" value={editingApp.participant?.rrn ? `${String(editingApp.participant.rrn).substring(0, 8)}...` : ''} readOnly style={styles.formInput} />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.formLabel}>사업명</label>
                  <input type="text" value={editingApp.project?.projectName || ''} readOnly style={styles.formInput} />
                </div>
              </div>
              {/* 연차 정보 */}
              {editLeaveSummary && (
                <div style={styles.formRow}>
                  <div style={styles.formCol}>
                    <label style={styles.formLabel}>생성연차</label>
                    <input type="text" value={`${editLeaveSummary.totalGeneratedHours || 0} h`} readOnly style={styles.formInput} />
                  </div>
                  <div style={styles.formCol}>
                    <label style={styles.formLabel}>누적사용연차</label>
                    <input type="text" value={`${editLeaveSummary.cumulativeUsedHours || 0} h`} readOnly style={styles.formInput} />
                  </div>
                  <div style={styles.formCol}>
                    <label style={styles.formLabel}>잔여연차</label>
                    <input type="text" value={`${editLeaveSummary.remainingHours || 0} h`} readOnly style={styles.formInput} />
                  </div>
                </div>
              )}
              {/* 신청 정보 */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>사용일자</label>
                <DateInput value={editForm.date} onChange={(v) => setEditForm(prev => ({ ...prev, date: v }))} inputStyle={styles.formInput} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>사용시간</label>
                <input
                  type="number"
                  name="hours"
                  value={editForm.hours}
                  onChange={handleEditFormChange}
                  placeholder="사용할 연차 시간을 입력하세요"
                  style={styles.formInput}
                  min="0.5"
                  step="0.5"
                />
              </div>
              {editLeaveSummary && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>사용 후 예상 잔여연차</label>
                  {(() => {
                    const expectedRemaining = (editLeaveSummary.remainingHours || 0) - parseFloat(editForm.hours || 0);
                    const isNegative = expectedRemaining < 0;
                    return (
                      <input
                        type="text"
                        value={`${expectedRemaining.toFixed(1)} h`}
                        readOnly
                        style={{ ...styles.formInput, ...(isNegative ? { color: 'red', fontWeight: 'bold' } : {}) }}
                      />
                    );
                  })()}
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>의견</label>
                <textarea
                  name="userComment"
                  value={editForm.userComment}
                  onChange={handleEditFormChange}
                  placeholder="의견을 입력하세요 (선택사항)"
                  style={{ ...styles.formInput, minHeight: '80px', resize: 'vertical' }}
                  rows="3"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>첨부파일 (PDF, 이미지)</label>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                  기존: {Array.isArray(editingApp.files) && editingApp.files.length > 0
                    ? editingApp.files.map((f) => f.originalname || f.filename).join(', ')
                    : '없음'} / 새로 선택 시 기존 파일을 대체합니다.
                </div>
                <input type="file" name="files" onChange={handleEditFormChange} style={styles.fileInput} multiple accept=".pdf,.jpg,.jpeg,.png,.gif" />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={handleCloseEditModal} style={styles.button}>취소</button>
              <button onClick={handleEditSubmit} style={{ ...styles.button, ...styles.saveButton }} disabled={editLoading}>
                {editLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 승인취소신청 모달 */}
      {cancelRequestApp && (
        <div style={styles.modalOverlay} onClick={handleCloseCancelRequestModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>승인취소 신청</h3>
              <button onClick={handleCloseCancelRequestModal} style={styles.button}>×</button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ marginBottom: '12px' }}>
                {cancelRequestApp.participant?.name}님의 {formatUsageDateList(cancelRequestApp)} 연차 사용 {formatUsageTimeCell(cancelRequestApp)} 승인을 취소 신청합니다.
              </p>
              {cancelRequestError ? <div style={styles.modalError}>{cancelRequestError}</div> : null}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>사유 (선택사항)</label>
                <textarea
                  value={cancelRequestComment}
                  onChange={(e) => setCancelRequestComment(e.target.value)}
                  placeholder="승인취소 사유를 입력하세요"
                  style={{ ...styles.formInput, minHeight: '80px', resize: 'vertical' }}
                  rows="3"
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={handleCloseCancelRequestModal} style={styles.button}>취소</button>
              <button
                onClick={handleSubmitCancelRequest}
                style={{ ...styles.button, ...styles.cancelRequestButton }}
                disabled={cancelRequestLoading}
              >
                {cancelRequestLoading ? '신청 중...' : '신청'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveApplicationInquiryPage;
